import { useState, useEffect, useCallback } from 'react';

export const useIntegerInputLogic = ({
  value,
  min,
  max,
  values,
  name,
  onChange
}) => {
  // Ensure value is within valid range and in our values array
  const normalizeValue = useCallback((val) => {
    const numVal = parseInt(val) || min;
    if (numVal < min) return min;
    if (numVal > max) return max;

    // Find closest valid value
    const closest = values.reduce((prev, curr) =>
      Math.abs(curr - numVal) < Math.abs(prev - numVal) ? curr : prev
    );
    return closest;
  }, [min, max, values]);

  const [currentValue, setCurrentValue] = useState(() => normalizeValue(value));

  // Update current value when prop changes
  useEffect(() => {
    const newValue = normalizeValue(value);
    if (newValue !== currentValue) {
      setCurrentValue(newValue);
    }
  }, [value, normalizeValue, currentValue]);

  const handleValueChange = useCallback((newValue) => {
    const normalizedValue = normalizeValue(newValue);
    setCurrentValue(normalizedValue);

    // Create synthetic event for form handling
    const syntheticEvent = {
      target: {
        name,
        value: normalizedValue,
        type: 'number'
      }
    };
    onChange?.(syntheticEvent);
  }, [normalizeValue, name, onChange]);

  const handleIncrement = useCallback(() => {
    setCurrentValue(prevValue => {
      const nextIndex = values.indexOf(prevValue) + 1;
      if (nextIndex < values.length) {
        const newValue = values[nextIndex];

        // Create synthetic event for form handling
        const syntheticEvent = {
          target: {
            name,
            value: newValue,
            type: 'number'
          }
        };
        onChange?.(syntheticEvent);

        return newValue;
      }
      return prevValue;
    });
  }, [values, name, onChange]);

  const handleDecrement = useCallback(() => {
    setCurrentValue(prevValue => {
      const currentIndex = values.indexOf(prevValue);
      if (currentIndex > 0) {
        const newValue = values[currentIndex - 1];

        // Create synthetic event for form handling
        const syntheticEvent = {
          target: {
            name,
            value: newValue,
            type: 'number'
          }
        };
        onChange?.(syntheticEvent);

        return newValue;
      }
      return prevValue;
    });
  }, [values, name, onChange]);

  return {
    currentValue,
    handleValueChange,
    handleIncrement,
    handleDecrement
  };
};
